'use client';

import clsx from 'clsx';

import { PAYMENT_METHODS } from '@/features/payment/const';
import { useToast } from '@/features/toast';
import { ProductTariffResponseV1 } from '@/shared/api';
import { priceFormat } from '@/shared/utils/price-format';

import { Button, CountdownTimer } from '../../ui';

interface TariffCardProps {
	tariff: ProductTariffResponseV1;
	productId: string;
}

export const TariffCard = ({ tariff, productId }: TariffCardProps) => {
	const isBasic = tariff.tariffId.includes('basic');
	const isPro = tariff.tariffId.includes('pro');
	const isAdvanced = tariff.tariffId.includes('advanced');
	const { addToast } = useToast();
	const hasAvailableSpots =
		tariff.tariffInfo.unlimitedPlaces || tariff.tariffInfo.places > 0;
	const showDiscount =
		tariff.tariffInfo.discountActive &&
		tariff.oneTimePaymentInfo.discountPrice !== tariff.oneTimePaymentInfo.price;

	const discountPercentage =
		((tariff.oneTimePaymentInfo.discountPrice -
			tariff.oneTimePaymentInfo.price) /
			tariff.oneTimePaymentInfo.discountPrice) *
		100;

	const getPaymentLink = ({
		tariffId,
		paymentMethod,
	}: {
		tariffId: string;
		paymentMethod: (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];
	}) => {
		if (hasAvailableSpots) {
			return `/payment/${productId}/${tariffId}?paymentMethod=${paymentMethod}`;
		}
		return '/system-design#tariffs';
	};

	const notAvailableSpotsHandler = () => {
		if (!hasAvailableSpots) {
			addToast('В данный момент нет доступных мест', '', 'info');
		}
	};

	return (
		<div
			className={clsx(
				'relative flex h-full flex-col overflow-hidden rounded-[40px] border p-6',
				{
					// База - белая карточка
					'border-gray-200 bg-white text-gray-900': isBasic,
					// Расширенный - темная карточка
					'border-gray-700 bg-gray-900 text-white': isPro,
					// Премиум - бело-серая карточка
					'border-gray-300 bg-gray-50 text-gray-900': isAdvanced,
				},
			)}
		>
			<div className="pb-4">
				<h3 className="text-2xl font-bold">
					{tariff.tariffInfo?.name ?? 'Не указано'}
				</h3>
			</div>

			{/* Features */}
			<div className="mb-6 space-y-3">
				{tariff.tariffInfo.features?.map((feature, index) => (
					<div key={index} className="flex items-start gap-2">
						<div
							className={clsx('mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full', {
								'bg-gray-600': isBasic,
								'bg-white': isPro,
								'bg-gray-500': isAdvanced,
							})}
						/>
						<span className="text-sm leading-relaxed">{feature}</span>
					</div>
				))}
			</div>

			<div className="mt-auto">
				{/* Price */}
				<div className="mb-6 space-y-2">
					<div className="flex flex-wrap items-center gap-3">
						{showDiscount ? (
							<>
								<span className="text-3xl font-bold">
									{priceFormat({
										value: tariff.oneTimePaymentInfo.discountPrice,
									})}
								</span>
								<div className="flex flex-wrap items-center gap-3">
									<span className="text-lg line-through opacity-60">
										{priceFormat({
											value: tariff.oneTimePaymentInfo.price,
										})}
									</span>
									<span className="rounded bg-green-500 px-2 py-1 text-xs text-white">
										{discountPercentage.toFixed(0)}%
									</span>
								</div>
							</>
						) : (
							<span className="text-3xl font-bold">
								{priceFormat({ value: tariff.oneTimePaymentInfo.price })}
							</span>
						)}
					</div>
					<div className="flex flex-col gap-1">
						<p className="text-sm opacity-80">
							{tariff.loanPaymentInfo.enabled
								? `от ${priceFormat({
										value: tariff.loanPaymentInfo.startingFromPerMonth,
										withCurrency: false,
									})} ₽/мес`
								: ''}
						</p>
						<p className="text-[12px] opacity-80">
							рассрочка от 3 до 6 месяцев
						</p>
					</div>
				</div>

				{/* Timer - показываем только если есть скидка и дата и дата не прошла */}
				{showDiscount &&
					tariff.tariffInfo.endDate &&
					new Date() < new Date(tariff.tariffInfo.discountEndDate) && (
						<div className="mb-6 space-y-3">
							<p className="text-sm font-medium">Скидка закончится через</p>
							<CountdownTimer
								targetDate={new Date(tariff.tariffInfo.discountEndDate)}
								theme={isPro ? 'dark' : 'light'}
							/>
						</div>
					)}

				{/* Buttons */}

				{tariff.tariffId !== 'algo-advanced' && (
					<div className="mb-6 space-y-3">
						{tariff.oneTimePaymentInfo.enabled && (
							<Button
								onClick={notAvailableSpotsHandler}
								href={getPaymentLink({
									tariffId: tariff.tariffId,
									paymentMethod: PAYMENT_METHODS.FULL,
								})}
								className="w-full"
								size="48"
								disabled={!hasAvailableSpots}
							>
								Оплатить полностью
							</Button>
						)}
						{tariff.loanPaymentInfo.enabled && (
							<Button
								onClick={notAvailableSpotsHandler}
								href={getPaymentLink({
									tariffId: tariff.tariffId,
									paymentMethod: PAYMENT_METHODS.LOAN,
								})}
								variant="ghost"
								className="w-full"
								size="48"
								disabled={!hasAvailableSpots}
							>
								Оформить рассрочку
							</Button>
						)}

						{tariff.dolyamiPaymentInfo.enabled && (
							<Button
								onClick={notAvailableSpotsHandler}
								variant="ghost"
								href={getPaymentLink({
									tariffId: tariff.tariffId,
									paymentMethod: PAYMENT_METHODS.DOLYAMI,
								})}
								size="48"
								className="w-full"
								disabled={!hasAvailableSpots}
							>
								Оплатить долями
							</Button>
						)}
					</div>
				)}

				{tariff.tariffId === 'algo-advanced' && (
					<div className="mb-6 space-y-3">
						<Button
							href={'https://forms.gle/7YZM6ATD2tEjmE5bA'}
							className="w-full"
							size="48"
						>
							Оставить заявку
						</Button>
					</div>
				)}

				<div className="text-sm opacity-80">
					{tariff.tariffInfo.unlimitedPlaces ? (
						<span>Мест: не ограничено</span>
					) : hasAvailableSpots ? (
						<span>Осталось мест: {tariff.tariffInfo.places}</span>
					) : (
						<span className="font-medium text-red-500">Мест не осталось</span>
					)}
				</div>
			</div>
		</div>
	);
};
